import { PaymentAccount } from 'src/services/payment-accounts'

interface ResolvePaymentAccountRefOptions {
  paymentAccountId?: string | null
  paymentAccountHint?: string | null
  paymentAccounts?: { results: PaymentAccount[] } | null
  paymentAccountLabel: (pa: PaymentAccount) => string
}

/**
 * Resolves the {value, label} ref for a prefilled payment account.
 * When an id is already known (e.g. from statement linking), the ref is built
 * immediately from the id/hint — it only upgrades to the richer formatted
 * label once the payment-accounts list resolves, it never waits on it.
 */
export function resolvePaymentAccountRef({
  paymentAccountId,
  paymentAccountHint,
  paymentAccounts,
  paymentAccountLabel,
}: ResolvePaymentAccountRefOptions): { value: string; label: string } | null {
  if (paymentAccountId) {
    const matchingAccount = paymentAccounts?.results?.find((pa) => pa.id === paymentAccountId)
    return {
      value: paymentAccountId,
      label: matchingAccount
        ? paymentAccountLabel(matchingAccount)
        : paymentAccountHint || paymentAccountId,
    }
  }

  if (paymentAccountHint && paymentAccounts?.results) {
    const hint = paymentAccountHint.toLowerCase()
    const matchingAccount = paymentAccounts.results.find((pa) => {
      if (pa.accountLastFour && hint.includes(pa.accountLastFour)) return true
      if (pa.name.toLowerCase().includes(hint) || hint.includes(pa.name.toLowerCase())) return true
      return false
    })
    if (matchingAccount) {
      return { value: matchingAccount.id, label: paymentAccountLabel(matchingAccount) }
    }
  }

  return null
}
